import Image from "next/image";
import { Loader2, Lock, Phone, Star, Zap } from "lucide-react";

import type { LandingMockCopy, LandingMockPart, LandingMockState } from "./landing-page-copy";

export type LandingMockVariant = {
  label: string;
  title: string;
  button: string;
};

type CcLandingMockProps = {
  copy: LandingMockCopy;
  state: LandingMockState;
  /** Part to highlight; the rest of the page dims a little. */
  focus?: LandingMockPart | null;
  /** Desktop browser window or a phone screen. */
  device?: "desktop" | "phone";
  /** An A/B variant: replaces headline and button and shows the variant tag. */
  variant?: LandingMockVariant;
  /** Load the photo eagerly (the hero) or lazily (further down). */
  priority?: boolean;
};

// One `sizes` for every copy of the photo, so the browser fetches it once.
const PHOTO_SIZES = "(min-width: 810px) 260px, 40vw";

/** Made-up logo mark for the made-up firm in the example. */
function BrandMark() {
  return (
    <svg className="cc-lp-mock__mark" viewBox="0 0 20 20" aria-hidden="true">
      <rect width="20" height="20" rx="6" />
      <path d="M6 10.5l2.6 2.6L14 7.5" />
    </svg>
  );
}

/**
 * A made-up campaign page drawn in HTML, in a browser window or on a phone.
 * Both versions are stacked in the same grid cell and cross-fade, so
 * switching between "before" and "after" never changes the height.
 * Decorative: hidden from screen readers, the surrounding copy says the same.
 */
export function CcLandingMock({
  copy,
  state,
  focus = null,
  device = "desktop",
  variant,
  priority = false,
}: CcLandingMockProps) {
  const { before, after } = copy;
  const title = variant?.title ?? after.title;
  const button = variant?.button ?? after.button;

  return (
    <div
      className="cc-lp-mock"
      data-state={state}
      data-device={device}
      data-variant={variant ? "true" : undefined}
      data-focus={focus ?? undefined}
      aria-hidden="true"
    >
      <div className="cc-lp-mock__chrome">
        <i />
        <i />
        <i />
        <span className="cc-lp-mock__url">
          <Lock size={9} strokeWidth={2.4} />
          {copy.url}
        </span>
        <span className="cc-lp-mock__speed" data-part="speed">
          <span className="cc-lp-mock__speed-before">
            <Loader2 size={10} strokeWidth={2.4} />
            {before.loading}
          </span>
          <span className="cc-lp-mock__speed-after">
            <Zap size={10} strokeWidth={2.4} />
            {after.speed}
          </span>
        </span>
      </div>

      <div className="cc-lp-mock__stack">
        {/* Before: menu, vague headline, competing buttons, long form, no proof, slow photo. */}
        {device === "desktop" && !variant ? (
          <div className="cc-lp-mock__layer cc-lp-mock__layer--before">
            <div className="cc-lp-mock__bar" data-part="nav">
              <b className="cc-lp-mock__logo cc-lp-mock__logo--plain">{copy.brand}</b>
              <span className="cc-lp-mock__nav">
                {before.nav.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </span>
            </div>
            <div className="cc-lp-mock__body">
              <div className="cc-lp-mock__copy">
                <strong className="cc-lp-mock__title" data-part="headline">
                  {before.title}
                </strong>
                <p className="cc-lp-mock__text">{before.text}</p>
                <span className="cc-lp-mock__buttons" data-part="cta">
                  {before.buttons.map((label) => (
                    <em key={label}>{label}</em>
                  ))}
                </span>
                <span className="cc-lp-mock__form cc-lp-mock__form--long" data-part="form">
                  {before.fields.map((field) => (
                    <span key={field}>{field}</span>
                  ))}
                </span>
                <span className="cc-lp-mock__trust cc-lp-mock__trust--empty" data-part="trust" />
              </div>
              <div className="cc-lp-mock__photo cc-lp-mock__photo--loading" />
            </div>
          </div>
        ) : null}

        {/* After: one message, one short form, one button, proof next to it, fast photo. */}
        <div className="cc-lp-mock__layer cc-lp-mock__layer--after">
          <div className="cc-lp-mock__bar" data-part="nav">
            <b className="cc-lp-mock__logo">
              <BrandMark />
              {copy.brand}
              <small>{copy.brandSuffix}</small>
            </b>
            <span className="cc-lp-mock__variant" data-part="test">
              {variant?.label ?? after.variant}
            </span>
            <span className="cc-lp-mock__phone">
              <Phone size={9} strokeWidth={2.4} />
              {copy.phone}
            </span>
          </div>
          <div className="cc-lp-mock__body">
            <div className="cc-lp-mock__copy">
              <strong className="cc-lp-mock__title" data-part="headline">
                {title}
              </strong>
              <p className="cc-lp-mock__text">{after.text}</p>
              <span className="cc-lp-mock__form" data-part="form">
                {after.fields.map((field) => (
                  <span key={field}>{field}</span>
                ))}
                <em className="cc-lp-mock__cta" data-part="cta">
                  {button}
                </em>
                <small className="cc-lp-mock__consent">{after.consent}</small>
              </span>
              <span className="cc-lp-mock__trust" data-part="trust">
                <span className="cc-lp-mock__quote">
                  <Image src={after.avatar} alt="" width={48} height={48} unoptimized />
                  <span>
                    <span className="cc-lp-mock__stars">
                      {Array.from({ length: 5 }, (_, index) => (
                        <Star key={index} size={8} strokeWidth={0} />
                      ))}
                    </span>
                    <span className="cc-lp-mock__quote-text">{after.trust}</span>
                    <small>{after.quoteBy}</small>
                  </span>
                </span>
                <span className="cc-lp-mock__clients">
                  {after.clients.map((client) => (
                    <b key={client}>{client}</b>
                  ))}
                </span>
              </span>
            </div>
            <div className="cc-lp-mock__photo">
              <Image
                src={copy.photo.src}
                alt=""
                width={copy.photo.width}
                height={copy.photo.height}
                sizes={PHOTO_SIZES}
                quality={75}
                loading={priority ? "eager" : "lazy"}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
